import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BarcodeGenerationHistoryResponse } from '../../../models/barcodes/barcode-response.model';
import { BarcodePrimaryCode, BarcodePrimaryCodeDisplayName } from '../../../models/barcodes/barcode-primary-code.enum';
import {
  BarcodeIdCode,
  BarcodeIdCodeDisplayName,
  BarcodeMediumCode,
  BarcodeMediumCodeDisplayName,
  BarcodeWaterCode,
  BarcodeWaterCodeDisplayName,
} from '../../../models/barcodes/barcode-secondary-code.enum';
import { SortDirection } from '../../orders/table/sort-direction.enum';

@Component({
  selector: 'app-barcode-table',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnChanges {
  @Input() barcodeList: BarcodeGenerationHistoryResponse[] = [];

  isLoading = true;
  error: string | null = null;
  rowsRaw: BarcodeGenerationHistoryResponse[] = [];
  rows: BarcodeGenerationHistoryResponse[] = [];

  sortColumn = '';
  sortDirection: SortDirection = SortDirection.NONE;
  SortDirection = SortDirection;

  columns = [
    { key: 'primary', label: 'Klasse', align: 'left' },
    { key: 'secondary', label: 'Unterklasse', align: 'left' },
    { key: 'lastBarcode', label: 'Letzter Barcode', align: 'left' },
    { key: 'lastGeneratedDate', label: 'Zuletzt generiert', align: 'left' },
  ];
  private readonly secondaryMaps: Record<string, Record<string, string>> = {
    [BarcodePrimaryCode.MEDIUM_BARCODE]: BarcodeMediumCodeDisplayName as any,
    [BarcodePrimaryCode.ID_BARCODE]: BarcodeIdCodeDisplayName as any,
    [BarcodePrimaryCode.WATER_BARCODE]: BarcodeWaterCodeDisplayName as any,
  };

  primaryLabel(code: BarcodePrimaryCode): string {
    return BarcodePrimaryCodeDisplayName[code] ?? String(code);
  }

  secondaryLabel(
    primary: BarcodePrimaryCode | number | string,
    code: BarcodeIdCode | BarcodeMediumCode | BarcodeWaterCode
  ): string {
    const map = this.secondaryMaps[String(primary)] || {};
    return (map as any)[code] ?? String(code);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['barcodeList']) {
      this.rowsRaw = [...(this.barcodeList ?? [])];
      if (this.sortColumn && this.sortDirection !== SortDirection.NONE) {
        this.applySort();
      } else {
        this.rows = [...this.rowsRaw];
      }
      this.isLoading = false;
      this.error = null;
    }
  }

  setSort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection =
        this.sortDirection === SortDirection.ASC
          ? SortDirection.DESC
          : this.sortDirection === SortDirection.DESC
            ? SortDirection.NONE
            : SortDirection.ASC;
      if (this.sortDirection === SortDirection.NONE) {
        this.sortColumn = '';
      }
    } else {
      this.sortColumn = column;
      this.sortDirection = SortDirection.ASC;
    }
    this.applySort();
  }

  private applySort(): void {
    if (!this.sortColumn || this.sortDirection === SortDirection.NONE) {
      this.rows = [...this.rowsRaw];
      return;
    }

    const dir = this.sortDirection === SortDirection.ASC ? 1 : -1;

    const valueFor = (row: BarcodeGenerationHistoryResponse): string | number | Date => {
      switch (this.sortColumn) {
        case 'primary':
          return row.primaryCode as unknown as string;
        case 'secondary':
          return row.secondaryCode as unknown as string;
        case 'lastBarcode':
          return row.lastBarcode ?? '';
        case 'lastGeneratedDate':
          return (row.lastGeneratedDate as unknown as Date) ?? new Date(0);
        default:
          return '';
      }
    };

    this.rows = [...this.rowsRaw].sort((a, b) => {
      const va = valueFor(a);
      const vb = valueFor(b);

      if (va instanceof Date && vb instanceof Date) {
        return (va.getTime() - vb.getTime()) * dir;
      }

      if (typeof va === 'number' && typeof vb === 'number') {
        return (va - vb) * dir;
      }

      const sa = String(va).toLocaleLowerCase();
      const sb = String(vb).toLocaleLowerCase();
      if (sa < sb) return -1 * dir;
      if (sa > sb) return 1 * dir;
      return 0;
    });
  }
}
